import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { PageHeader, Card, Select } from '../ui';

function MatchCard({ home, away, homeScore, awayScore, label, isFinal }) {
  const played = homeScore !== null && homeScore !== undefined;
  const homeWin = played && homeScore > awayScore;
  const awayWin = played && awayScore > homeScore;
  return (
    <div style={{ background: 'var(--surface-0)', border: `1.5px solid ${isFinal ? 'var(--brand-gold)' : 'var(--border)'}`, borderRadius: 10, overflow: 'hidden', minWidth: 160, boxShadow: isFinal ? '0 0 0 3px rgba(249,168,37,0.15)' : 'var(--shadow-sm)' }}>
      {label && <div style={{ background: isFinal ? 'var(--brand-gold)' : 'var(--surface-2)', padding: '3px 10px', fontSize: 10, fontWeight: 700, color: isFinal ? '#fff' : 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>}
      <div style={{ padding: '7px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)', background: homeWin ? 'rgba(21,101,192,0.05)' : '' }}>
        {home ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: home.color, flexShrink: 0 }} />
              <span style={{ fontSize: 13, fontWeight: homeWin ? 700 : 500, color: homeWin ? 'var(--gold-deep)' : awayWin ? 'var(--text-muted)' : 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{home.name}</span>
            </div>
            <span style={{ fontSize: 14, fontWeight: 800, fontFamily: 'var(--font-condensed)', color: homeWin ? 'var(--gold-deep)' : 'var(--text-muted)', marginLeft: 8 }}>{played ? homeScore : '—'}</span>
          </>
        ) : <span style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>TBD</span>}
      </div>
      <div style={{ padding: '7px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: awayWin ? 'rgba(21,101,192,0.05)' : '' }}>
        {away ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: away.color, flexShrink: 0 }} />
              <span style={{ fontSize: 13, fontWeight: awayWin ? 700 : 500, color: awayWin ? 'var(--gold-deep)' : homeWin ? 'var(--text-muted)' : 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{away.name}</span>
            </div>
            <span style={{ fontSize: 14, fontWeight: 800, fontFamily: 'var(--font-condensed)', color: awayWin ? 'var(--gold-deep)' : 'var(--text-muted)', marginLeft: 8 }}>{played ? awayScore : '—'}</span>
          </>
        ) : <span style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>TBD</span>}
      </div>
    </div>
  );
}

function SingleElimBracket({ teamList }) {
  // Build a standard single-elim bracket for N teams (round up to power of 2)
  const n = teamList.length;
  if (n < 2) return <p style={{ color: 'var(--text-muted)', padding: '2rem', textAlign: 'center' }}>Need at least 2 teams for a bracket.</p>;

  const rounds = Math.ceil(Math.log2(n));
  const slots = Math.pow(2, rounds);
  const seeded = [...teamList, ...Array(slots - n).fill(null)]; // nulls = byes

  const roundLabels = ['Round 1', 'Quarterfinals', 'Semifinals', 'Final', 'Championship'].slice(-(rounds));
  if (roundLabels.length < rounds) {
    for (let i = roundLabels.length; i < rounds; i++) roundLabels.unshift(`Round ${i + 1}`);
  }

  // Build matchups per round
  const buildRound = (teams) => {
    const matches = [];
    for (let i = 0; i < teams.length; i += 2) matches.push([teams[i], teams[i + 1]]);
    return matches;
  };

  const round1 = buildRound(seeded);

  return (
    <div style={{ display: 'flex', gap: 32, overflowX: 'auto', padding: '1rem 0' }}>
      {/* Round 1 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10, textAlign: 'center' }}>{roundLabels[0]}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {round1.map((match, i) => (
            <MatchCard key={i} home={match[0]} away={match[1]} isFinal={false} />
          ))}
        </div>
      </div>

      {/* Subsequent rounds — TBD */}
      {Array.from({ length: rounds - 1 }, (_, ri) => {
        const matchCount = Math.pow(2, rounds - ri - 2);
        return (
          <div key={ri} style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10, textAlign: 'center' }}>{roundLabels[ri + 1]}</div>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-around', flex: 1, gap: 12 }}>
              {Array.from({ length: matchCount }, (_, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', height: matchCount === 1 ? 'auto' : undefined }}>
                  <MatchCard home={null} away={null} isFinal={ri === rounds - 2} label={ri === rounds - 2 ? '🏆 Final' : undefined} />
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DoubleElimBracket({ teamList }) {
  const teams = teamList.slice(0, 8);
  const half = Math.ceil(teams.length / 2);
  const wbR1 = [];
  for (let i = 0; i < teams.length; i += 2) wbR1.push([teams[i], teams[i + 1] || null]);

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        {/* Winners bracket */}
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--gold-deep)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: 'var(--gold-deep)' }} />
            Winners bracket
          </div>
          <div style={{ display: 'flex', gap: 20, overflowX: 'auto' }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Round 1</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {wbR1.map((m, i) => <MatchCard key={i} home={m[0]} away={m[1]} />)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Semifinals</div>
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-around', gap: 10, paddingTop: 30 }}>
                {Array.from({ length: Math.ceil(wbR1.length / 2) }, (_, i) => <MatchCard key={i} home={null} away={null} />)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>WB Final</div>
              <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <MatchCard home={null} away={null} />
              </div>
            </div>
          </div>
        </div>

        {/* Losers bracket */}
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#c62828', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: '#e53935' }} />
            Losers bracket
          </div>
          <div style={{ display: 'flex', gap: 20, overflowX: 'auto' }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>LB Round 1</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {Array.from({ length: half }, (_, i) => <MatchCard key={i} home={null} away={null} />)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>LB Finals</div>
              <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <MatchCard home={null} away={null} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Grand final */}
      <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--brand-gold)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>🏆 Grand Final</div>
        <MatchCard home={null} away={null} label="Grand Final" isFinal />
      </div>
    </div>
  );
}

export default function Brackets() {
  const { tournaments, teams } = useApp();
  const [selectedTrn, setSelectedTrn] = useState(tournaments[0]?.id || '');
  const [selectedAG, setSelectedAG] = useState('');

  const tournament = tournaments.find(t => t.id === selectedTrn);
  const ageGroup = tournament?.ageGroups?.find(ag => ag.group === selectedAG) || tournament?.ageGroups?.[0];

  const agOptions = tournament?.ageGroups || [];
  const currentAG = ageGroup || agOptions[0];
  const registeredTeams = currentAG?.teams?.map(id => teams.find(t => t.id === id)).filter(Boolean) || [];

  return (
    <div>
      <PageHeader title="Brackets" subtitle="Visual tournament brackets — single and double elimination" />

      {/* Selectors */}
      <div style={{ display: 'flex', gap: 10, marginBottom: '1.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <Select style={{ width: 'auto' }} value={selectedTrn} onChange={e => { setSelectedTrn(e.target.value); setSelectedAG(''); }}>
          <option value="">Select tournament…</option>
          {tournaments.filter(t => t.type !== 'league').map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </Select>
        {tournament && (
          <Select style={{ width: 'auto' }} value={selectedAG || currentAG?.group || ''} onChange={e => setSelectedAG(e.target.value)}>
            {agOptions.map(ag => <option key={ag.group} value={ag.group}>{ag.group} — {ag.format?.replace(/_/g, ' ')}</option>)}
          </Select>
        )}
      </div>

      {!tournament ? (
        <Card><p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '3rem' }}>Select a tournament to view its bracket</p></Card>
      ) : !currentAG ? (
        <Card><p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '3rem' }}>No age groups configured for this tournament</p></Card>
      ) : (
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.25rem', paddingBottom: '1rem', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontFamily: 'var(--font-condensed)', fontWeight: 800, fontSize: 20 }}>{currentAG?.group}</span>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}>{currentAG?.format?.replace(/_/g, ' ')}</span>
            <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--text-muted)' }}>{registeredTeams.length} / {currentAG?.maxTeams} teams registered</span>
          </div>

          {registeredTeams.length < 2 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>🏆</div>
              <p style={{ fontWeight: 700, color: 'var(--text-secondary)' }}>Bracket not yet set</p>
              <p style={{ fontSize: 14, marginTop: 4 }}>Need at least 2 registered teams to generate a bracket</p>
            </div>
          ) : currentAG?.format === 'double_elimination' ? (
            <DoubleElimBracket teamList={registeredTeams} />
          ) : (
            <SingleElimBracket teamList={registeredTeams} />
          )}
        </Card>
      )}
    </div>
  );
}
